/* src/components/pages/Logsviewer.jsx - Modificado el 08-02-2026 */
/* ------------- Consulta los logs de la aplicación ------------- */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { TextField, Box, MenuItem, Button, Tooltip, Typography, Stack } from '@mui/material';

import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { CustomToolbar } from "../../components/datagrid/CustomToolbar";
// import DataGridSkeleton from "../../components/datagrid/DataGridSkeleton";

import { RiFolderDownloadLine, RiFileSearchLine, RiArrowLeftFill , RiArrowRightFill } from "react-icons/ri";

import { useAuth } from "../../context/AuthContext";
import { useFeedback } from '../../context/FeedbackContext'; // Mensajes breves

const Logviewer = ({ modo }) => {
  const { user } = useAuth();
  const { showFeedback } = useFeedback();

  const [files, setFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState("");
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [searchDate, setSearchDate] = useState("");

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState(null);

  /* Cargar lista de archivos logs */
  useEffect(() => {
    try {
      (async () => {
        try {
          const response = await api.post('/logs/getlistardir', {
            }, /* body vacío */
          );

          if (response.data.success) {
            setFiles(response.data.files);
          } else {
            setFiles([]);

            showFeedback({
              message: 'No se encontraron archivos de Logs.',
              severity: 'warning',
            });

          }
        } catch (error) {
          console.error("Error al cargar enrolados:", error);
          let textMessage = ''
          if (error.code === 'ECONNABORTED') {
            textMessage = 'La solicitud tardó demasiado en responder (timeout).';
          } else if (error.response) {
            textMessage = `Error al obtener archivos Logs: ${error.response.statusText}`;
          } else {
            textMessage = 'No se pudo conectar con el servidor.';
          }

          showFeedback({
            message: textMessage,
            severity: 'error',
          });

        }
      })();
    } catch (error) {
      showFeedback({
        message: 'No fue posible obtener su solicitud, por favor intente más tarde',
        severity: 'error',
      });
    }
  }, [showFeedback]);

  /* Buscar datos del archivo log */
  const fetchLogs = useCallback(async (newPage = 1) => {
    if (!selectedFile) {
      showFeedback({
        message: 'Debes seleccionar un archivo',
        severity: 'warning',
      });
      return;
    }

    try {
      setLoading(true);

      const response = await api.post('/logs/getbuscarlogs', {
        filename: selectedFile,
         date: searchDate,
         page: newPage,
         limit: 19
        },
      );

      if (response.data.success) {
        /* Formatear los logs en objetos para MRT */
        const formattedLogs = response.data.logs.map((line, idx) => ({
          linea: (newPage - 1) * 19 + idx + 1,
          contenido: line,
        }));

        setLogs(formattedLogs);
        setPagination(response.data.pagination);
        setPage(newPage);

        if (response.data.logs.length === 0) {
          showFeedback({
            message: 'No se encontraron logs con esa fecha',
            severity: 'warning',
          });
        }
      } else {
        showFeedback({
          message: 'No fue posible obtener su solicitud, por favor intente más tarde',
          severity: 'error',
        });
      }
    } catch (err) {
      showFeedback({
        message: 'No fue posible obtener su solicitud, por favor intente más tarde',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  }, [selectedFile, searchDate, showFeedback]);


  /* Descargar el archivo de log y mostrarlo en una nueva pestaña en el navegador */
  const handleOpenLog = async () => {
    if (!selectedFile) {
      showFeedback({
        message: 'Debes seleccionar un archivo',
        severity: 'warning',
      });
    }

    try {
      const response = await api.post('/logs/descargarlogs', {
        filename: selectedFile
        },
        {
          responseType: "text"
        }
      );

      const logContent = response.data;

      /* Crear un Blob con el contenido del log */
      const blob = new Blob([logContent], { type: "text/plain;charset=utf-8" });

      /* Crear URL temporal */
      const blobUrl = URL.createObjectURL(blob);

      /* Abrir en nueva pestaña */
      const newWindow = window.open(blobUrl, "_blank");

      if (!newWindow) {
        showFeedback({
          message: 'El navegador bloqueó la ventana emergente. Desactiva el bloqueador de pop-ups.',
          severity: 'warning',
        });
      }

      /* Cambiar el título (cuando la pestaña cargue el blob) */
      newWindow.onload = () => {
        newWindow.document.title = selectedFile;
      };

    } catch (err) {
      showFeedback({
        message: 'No fue posible abrir el log.',
        severity: 'error',
      });
    }
  };

  /* Configuración de MUI DataGrid */
  const columns = useMemo(() => [
    {
      field: 'linea',
      width: 80,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="N° Línea" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            N° Línea
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'contenido',
      width: 1300,
      sortable: true,
      align: 'center',
      headerAlign: 'center',
      renderHeader: () => (
        <Tooltip title="Contenido del Log" arrow
          placement="top"
        >
        <span
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              fontWeight: 'bold',
              cursor: 'help',
            }}
          >
            Contenido del Log
          </span>
        </Tooltip>
      ),
    },
  ], []);

  /* Render */
  return (
    <Box sx={{ width: '100%', pt: 0, overflow: 'visible' }}>
      {/* Barra de Controles */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,
            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
              textAlign: 'center',
              letterSpacing: '0.03em',
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <TextField
          label="Cliente"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <TextField
          select
          id="log-selector"
          label="Seleccionar Archivo"
          value={selectedFile}
          size="small"
          onChange={(e) => setSelectedFile(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          variant="outlined"
          sx={{
            minWidth: 260,
            flexGrow: 1,
            "& .MuiInputBase-root": {
              backgroundColor: (t) => t.custom.ui.readonlyBg,
            },
          }}
        >
          {files.length > 0 ? (
            files.map((file, idx) => (
              <MenuItem key={idx} value={file}>{file}</MenuItem>
            ))
          ) : (
            <MenuItem disabled>No hay registros</MenuItem>
          )}
        </TextField>

        {/* Selector de fecha */}
        <TextField
          type="date"
          id="fecha-selector"
          label=""
          value={searchDate}
          size="small"
          onChange={(e) => setSearchDate(e.target.value)}
          slotProps={{ inputLabel: { shrink: true } }}
          variant="outlined"
          placeholder="Todas"
          // helperText={!searchDate ? "Todas las fechas" : ""}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            "& .MuiInputBase-root": {
              backgroundColor: (t) => t.custom.ui.readonlyBg,
            },
          }}
        />

        {/* Botón Buscar */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine size={18} />}
          onClick={() => fetchLogs(1)}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,

            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            color: (theme) => theme.custom.ui.primaryActionText,

            '&:hover': {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          Buscar
        </Button>


        {/* Botón Descargar */}
        <Tooltip title="Descargar" placement="top" arrow>
          <span>
            <Button 
              variant="contained"
              disabled={logs.length ===0}
              onClick={handleOpenLog}
              sx={{
                minWidth: 110,
                height: 40,
                flexShrink: 0,

                backgroundColor: (theme) => theme.custom.ui.primaryAction,
                color: (theme) => theme.custom.ui.primaryActionText,

                '&:hover': {
                  backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
                },
              }}
              >
              <RiFolderDownloadLine className="m-2" style={{ fontSize: '1.5rem' }} />
              Descargar
            </Button>
          </span>
        </Tooltip>
      </Stack>


      {/* Configuración MIU DataGrid */}
      <Box sx={{ height: "calc(100vh - 220px)", minHeight: 0 }}>
        <DataGrid
          rows={logs}
          columns={columns}
          getRowId={(row) => row.linea}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            flex: 1,
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />
      </Box>



      {pagination && pagination.total > 0 && (
        <Box 
          sx={{ 
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            mt: 1,
            mb: 0,
            maxWidth: "90%",
            mx: "auto",
          }}
        >
          {/* Texto centrado */}
          <Box sx={{ flexGrow: 1, textAlign: "center" }}>
            Página {pagination.page} de {pagination.totalPages} ({pagination.total} registros)
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography>Ir a Página</Typography>

            <TextField
              type="number"
              size="small"
              value={page}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (!isNaN(val)) {
                  const newPage = Math.min(Math.max(val, 1), pagination.totalPages);
                  setPage(newPage);
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  fetchLogs(page);
                }
              }}
              sx={{
                ml: 1,
                height: '40px',
                width: 80,
                '& input': {
                  textAlign: 'center',
                  color: '#ca531f',
                  MozAppearance: 'textfield', // Para Firefox
                },
              }}
              slotProps={{
                input: {
                  min: 1,
                  max: pagination.totalPages,
                }
              }}
            />
          </Box>

          {/* Botón Anterior */}
          <Button 
            variant="outlined" 
            disabled={page <= 1} 
            onClick={() => fetchLogs(page - 1)}
            sx={{
              width: '150px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#8b888e', // Color de fondo inicial
              color: 'white', // Color del texto en blanco
              '&:hover': {
                backgroundColor: '#65a76c', // Cambia el color de fondo a verde al pasar el mouse //
                color: 'white', // Cambia el color del texto a blanco //
              }
            }}
          >
            <RiArrowLeftFill  className="m-2" style={{ fontSize: '1.5rem' }} />
            Anterior
          </Button>

          {/* Botón Siguiente alineado a la derecha */}
          <Button 
            variant="outlined" 
            disabled={page >= pagination.totalPages} 
            onClick={() => fetchLogs(page + 1)}
            sx={{
              width: '150px',
              height: '40px',
              textTransform: 'none',
              backgroundColor: '#8b888e', // Color de fondo inicial
              color: 'white', // Color del texto en blanco
              '&:hover': {
                backgroundColor: '#65a76c', // Cambia el color de fondo a verde al pasar el mouse //
                color: 'white', // Cambia el color del texto a blanco //
              }
            }}
          >
            <RiArrowRightFill className="m-2" style={{ fontSize: '1.5rem' }} />
            Siguiente
          </Button>
        </Box>
      )}

    </Box>
  );
}

export default Logviewer;
